import { Avatar, Box, CircularProgress, Grid } from "@mui/material";
import React, { useRef, useState } from "react";
import { useEffect } from "react";
import { getProfileInfo } from "../../../utils/apiHelperInternal";
import { POLL_DURARION, POLL_INTERVAL } from "../../../utils/constants";
import { formatNumber, timeConvert } from "../../../utils/helper";
import "./AccountIdentityData.scss";

const IDENTITY_ATTRIBUTES = {
  profile_pic: "profile_pic",
  platform_profile_name: "platform_profile_name",
  subscriber_count: "subscriber_count",
  name: "name",
  email_id: "email_id",
  username: "username",
  playlists: "playlists",
  videos: "videos",
  total_watch_time: "total_watch_time",
  follower_count: "follower_count",
  posts: "posts",
  tweets: "tweets",
};

const IDENTITY_WORKPLATFORMS_MAPPING = {
  YouTube: [
    "profile_pic",
    "platform_profile_name",
    "subscriber_count",
    "name",
    "email_id",
    "username",
    "playlists",
    "videos",
    "total_watch_time",
  ],
  Instagram: [
    "profile_pic",
    "platform_profile_name",
    "follower_count",
    "name",
    "email_id",
    "username",
    "posts",
  ],
  "Instagram Direct": [
    "profile_pic",
    "platform_profile_name",
    "follower_count",
    "name",
    "email_id",
    "username",
    "posts",
  ],
  "Instagram Lite": [
    "profile_pic",
    "platform_profile_name",
    "follower_count",
    "name",
    "email_id",
    "username",
    "posts",
  ],
  TikTok: [
    "profile_pic",
    "platform_profile_name",
    "follower_count",
    "email_id",
    "username",
    "posts",
  ],
  Twitter: [
    "profile_pic",
    "platform_profile_name",
    "follower_count",
    "name",
    "username",
    "tweets",
  ],
  Facebook: [
    "profile_pic",
    "platform_profile_name",
    "follower_count",
    "name",
    "email_id",
    "username",
  ],
  others: [
    "profile_pic",
    "platform_profile_name",
    "subscriber_count",
    "name",
    "email_id",
    "username",
  ],
};

function AccountIdentityData({ selectedPlatform }) {
  const [identityData, setIdentityData] = useState(null);
  const [currWorkPlatformMetrics, setCurrWorkPlatformMetrics] = useState(null);
  const isCompMounted = useRef();
  const pollId = useRef();
  const pollStartTime = useRef();

  async function getIdentityData() {
    const currIdentityData = await getProfileInfo(selectedPlatform);
    if (currIdentityData.data.length !== 0 && isCompMounted.current)
      setIdentityData(currIdentityData.data[0]);
  }

  useEffect(() => {
    isCompMounted.current = true;
    if (selectedPlatform) {
      getIdentityData();
      pollStartTime.current = Date.now();
      pollId.current = setInterval(() => {
        if (Date.now() - pollStartTime.current < POLL_DURARION)
          getIdentityData();
        else window.clearInterval(pollId.current);
      }, POLL_INTERVAL);
    }
    return () => {
      isCompMounted.current = false;
      window.clearInterval(pollId.current);
    };
  }, [selectedPlatform]);

  useEffect(() => {
    if (identityData) {
      const work_platform_name = identityData.work_platform.name;
      if (IDENTITY_WORKPLATFORMS_MAPPING.hasOwnProperty(work_platform_name)) {
        setCurrWorkPlatformMetrics(
          IDENTITY_WORKPLATFORMS_MAPPING[work_platform_name]
        );
      } else {
        setCurrWorkPlatformMetrics(IDENTITY_WORKPLATFORMS_MAPPING["others"]);
      }
    }
  }, [identityData]);

  return (
    <>
      {identityData && currWorkPlatformMetrics && (
        <Box className="container">
          <Box
            sx={{
              height: "80px",
              width: "100%",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              marginBottom: "56px",
            }}
          >
            <Box>
              <Avatar
                alt="Profile Pic"
                src={identityData.image_url}
                className="profilePic"
              />
            </Box>
            <Box>
              <Box className="userName">
                {identityData.platform_profile_name !== null
                  ? identityData.platform_profile_name
                  : "-"}
              </Box>
              {currWorkPlatformMetrics.includes(
                IDENTITY_ATTRIBUTES.subscriber_count
              ) && (
                <Box className="userSubcribers">
                  {identityData.reputation.subscriber_count !== null
                    ? formatNumber(identityData.reputation.subscriber_count)
                    : "-"}
                  <p>SUBSCRIBERS</p>
                </Box>
              )}
              {currWorkPlatformMetrics.includes(
                IDENTITY_ATTRIBUTES.follower_count
              ) && (
                <Box className="userSubcribers">
                  {identityData.reputation.follower_count !== null
                    ? formatNumber(identityData.reputation.follower_count)
                    : "-"}
                  <p>FOLLOWERS</p>
                </Box>
              )}
            </Box>
          </Box>
          <Grid
            rowSpacing={{ xs: 4 }}
            columnSpacing={{ xs: 15 }}
            container
            spacing={{ xs: 2, md: 2 }}
            columns={{ xs: 2, sm: 8, md: 14 }}
            className="dataGrid"
          >
            <Grid className="gridKey" item xs={2} sm={4} md={4}>
              <Box className="userInfoKey">NAME</Box>
              <Box>
                {identityData.full_name !== null ? identityData.full_name : "-"}
              </Box>
            </Grid>
            <Grid className="gridKey" item xs={2} sm={4} md={4}>
              <Box className="userInfoKey">USERNAME</Box>
              <Box className="text-truncate">
                {identityData.platform_username !== null
                  ? identityData.platform_username
                  : "-"}
              </Box>
            </Grid>
            {currWorkPlatformMetrics.includes(IDENTITY_ATTRIBUTES.videos) && (
              <Grid className="gridKey" item xs={2} sm={4} md={4}>
                <Box className="userInfoKey">VIDEOS</Box>
                <Box>
                  {identityData.reputation.content_count !== null
                    ? formatNumber(identityData.reputation.content_count)
                    : "-"}
                </Box>
              </Grid>
            )}
            {currWorkPlatformMetrics.includes(IDENTITY_ATTRIBUTES.posts) && (
              <Grid className="gridKey" item xs={2} sm={4} md={4}>
                <Box className="userInfoKey">POSTS</Box>
                <Box>
                  {identityData.reputation.content_count !== null
                    ? formatNumber(identityData.reputation.content_count)
                    : "-"}
                </Box>
              </Grid>
            )}
            {currWorkPlatformMetrics.includes(IDENTITY_ATTRIBUTES.tweets) && (
              <Grid className="gridKey" item xs={2} sm={4} md={4}>
                <Box className="userInfoKey">TWEETS</Box>
                <Box>
                  {identityData.reputation.content_count != null
                    ? formatNumber(identityData.reputation.content_count)
                    : "-"}
                </Box>
              </Grid>
            )}
            {currWorkPlatformMetrics.includes(IDENTITY_ATTRIBUTES.email_id) && (
              <Grid className="gridKey" item xs={2} sm={4} md={4}>
                <Box className="userInfoKey">EMAIL ID</Box>
                <Box className="text-truncate">
                  {(identityData.emails.length > 0 &&
                    identityData.emails[0].email_id) ||
                    "-"}
                </Box>
              </Grid>
            )}
            {currWorkPlatformMetrics.includes(
              IDENTITY_ATTRIBUTES.playlists
            ) && (
              <Grid className="gridKey" item xs={2} sm={4} md={4}>
                <Box className="userInfoKey">PLAYLISTS</Box>
                <Box>
                  {identityData.reputation.content_group_count !== null
                    ? formatNumber(identityData.reputation.content_group_count)
                    : "-"}
                </Box>
              </Grid>
            )}
            {currWorkPlatformMetrics.includes(
              IDENTITY_ATTRIBUTES.total_watch_time
            ) && (
              <Grid className="gridKey" item xs={2} sm={4} md={4}>
                <Box className="userInfoKey">TOTAL WATCH TIME</Box>
                <Box>
                  {identityData.reputation.watch_time_in_hours != null
                    ? timeConvert(identityData.reputation.watch_time_in_hours)
                    : "-"}
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </>
  );
}

export default AccountIdentityData;
